import React, { useState } from "react";
import { Mail, MessageCircle, Instagram } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { WaveDivider, Paw } from "@/components/decor";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  useSeo("Contact Us", "Get in touch with The Happy Bulldog Co.");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll be in touch soon 🐾");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <section className="relative bg-[#FF6B9D]/15 py-14 text-center overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <span className="font-hand text-2xl text-[#FF6B9D]">Say hello</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#19324A] mt-1">Get in Touch</h1>
          <p className="mt-3 text-[#19324A]/70">Questions, ideas, or just want to say hi to Chewie & Solo? We'd love to hear from you.</p>
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-[1fr_320px] gap-8">
        <form onSubmit={submit} className="bg-white rounded-3xl soft-shadow p-6 sm:p-8 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Name</label><input required value={form.name} onChange={set("name")} className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FFD93D]" data-testid="contact-name" /></div>
            <div><label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Email</label><input required type="email" value={form.email} onChange={set("email")} className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FFD93D]" data-testid="contact-email" /></div>
          </div>
          <div><label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Subject</label><input value={form.subject} onChange={set("subject")} className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FFD93D]" data-testid="contact-subject" /></div>
          <div><label className="block text-sm font-semibold text-[#19324A]/70 mb-1">Message</label><textarea required rows={5} value={form.message} onChange={set("message")} className="w-full rounded-2xl border-2 border-[#19324A]/10 px-4 py-3 outline-none focus:border-[#FFD93D] resize-none" data-testid="contact-message" /></div>
          <button type="submit" disabled={loading} className="btn-pink w-full" data-testid="contact-submit">{loading ? "Sending…" : "Send Message"}</button>
        </form>

        <div className="space-y-4">
          <div className="bg-[#FFD93D]/25 rounded-3xl p-6">
            <Paw className="w-10 h-10 text-[#FF6B9D]" />
            <h3 className="font-display font-bold text-[#19324A] mt-3">A small Australian brand</h3>
            <p className="text-sm text-[#19324A]/70 mt-1">Run with love (and a lot of biscuits) by Karen, Chewie & Solo.</p>
          </div>
          <div className="bg-white rounded-3xl soft-shadow p-6 space-y-3">
            <a href="mailto:hello@thehappybulldogco.com.au" className="flex items-center gap-3 text-[#19324A]/80 hover:text-[#FF6B9D] transition-colors"><Mail className="w-5 h-5 text-[#4CC9F0]" /><span className="text-sm">hello@thehappybulldogco.com.au</span></a>
            <div className="flex items-center gap-3 text-[#19324A]/80"><Instagram className="w-5 h-5 text-[#FF6B9D]" /><span className="text-sm">@thehappybulldogco (coming soon)</span></div>
            <div className="flex items-center gap-3 text-[#19324A]/80"><MessageCircle className="w-5 h-5 text-[#7ED957]" /><span className="text-sm">We reply as fast as a bulldog chasing biscuits.</span></div>
          </div>
        </div>
      </section>
    </div>
  );
}
